import { useCallback, useEffect } from 'react'

/**
 * Custom hook for carousel keyboard navigation.
 * Handles ArrowLeft (previous), ArrowRight (next), and Escape (close) keys.
 *
 * @param {Object} options
 * @param {React.RefObject} options.carouselRef - Ref to the carousel component (react-slick)
 * @param {boolean} options.isOpen - Whether the carousel overlay is open
 * @param {Function} options.onClose - Callback to close the overlay
 */
function useCarouselKeyboardNavigation({ carouselRef, isOpen, onClose }) {
  const handleKeyDown = useCallback(
    (event) => {
      if (!isOpen) {
        return
      }

      switch (event.key) {
        case 'ArrowLeft':
          if (carouselRef.current) {
            carouselRef.current.slickPrev()
          }
          break
        case 'ArrowRight':
          if (carouselRef.current) {
            carouselRef.current.slickNext()
          }
          break
        case 'Escape':
          onClose()
          break
        default:
          break
      }
    },
    [isOpen, onClose, carouselRef],
  )

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => {
        document.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [isOpen, handleKeyDown])
}

export default useCarouselKeyboardNavigation
